// src/pages/customers/index.jsx
import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import api from '../../core/api';
import './Customers.css';
import CustomersSkeleton from './CustomersSkeleton';

const EMPTY_CUSTOMER = {
  name: '',
  national: '',
  eco: '',
  postal: '',
  province: '',
  city: '',
  address: '',
  phone: '',
};

const Customers = () => {
  const [customers, setCustomers] = useState([]);
  const [form, setForm] = useState(EMPTY_CUSTOMER);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('success');

  const nameInputRef = useRef(null);

  /* ============== Fetch ============== */
  const fetchCustomers = async () => {
    try {
      const res = await api.get('/customers');
      const list = res.data?.customers || res.data || [];
      setCustomers(Array.isArray(list) ? list : []);
    } catch (err) {
      console.error('خطا در دریافت مشتریان:', err);
      showMessage('خطا در دریافت مشتریان', 'danger');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCustomers();
  }, []);

  /* ============== Toast auto-hide ============== */
  const showMessage = (text, type = 'success') => {
    setMessage(text);
    setMessageType(type);
  };

  useEffect(() => {
    if (!message) return;
    const t = setTimeout(() => setMessage(''), 3000);
    return () => clearTimeout(t);
  }, [message]);

  /* ============== Handlers ============== */
  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return;

    if (!form.phone.trim()) {
      showMessage('شماره موبایل الزامی است', 'danger');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        national: form.national.trim(),
        eco: form.eco.trim(),
        postal: form.postal.trim(),
        province: form.province.trim(),
        city: form.city.trim(),
        address: form.address.trim(),
        phone: form.phone.trim(),
      };

      const res = await api.post('/customers', payload);
      const created = res.data?.customer || res.data;

      if (created && created.id) {
        setCustomers((prev) => [created, ...prev]);
      } else {
        await fetchCustomers();
      }

      setForm(EMPTY_CUSTOMER);
      showMessage('مشتری با موفقیت اضافه شد', 'success');

      // فوکوس روی اولین فیلد
      if (nameInputRef.current) nameInputRef.current.focus();
    } catch (err) {
      console.error('خطا در افزودن مشتری:', err);
      showMessage(
        err.response?.data?.error || 'خطا در افزودن مشتری',
        'danger'
      );
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('حذف شود؟')) return;

    setDeletingId(id);
    try {
      await api.post(`/customers/${id}/delete`);
      setCustomers((prev) => prev.filter((c) => c.id !== id));
      showMessage('مشتری حذف شد', 'success');
    } catch (err) {
      console.error('خطا در حذف مشتری:', err);
      showMessage(
        err.response?.data?.error || 'خطا در حذف مشتری',
        'danger'
      );
    } finally {
      setDeletingId(null);
    }
  };

  /* ============== Render ============== */
  if (loading) {
    return <CustomersSkeleton />;
  }

  return (
    <>
      {message && (
        <div
          className={
            messageType === 'danger' ? 'toast-danger' : 'toast-success'
          }
        >
          {message}
        </div>
      )}

      {/* ============ فرم افزودن مشتری ============ */}
      <div className="card">
        <h2>افزودن مشتری</h2>
        <form onSubmit={handleSubmit} id="addCustomerForm">
          <div className="grid">
            <div>
              <label>نام شخص حقیقی/حقوقی *</label>
              <input
                ref={nameInputRef}
                name="name"
                value={form.name}
                onChange={handleChange}
                required
              />
            </div>
            <div>
              <label>شماره ثبت/شماره ملی</label>
              <input
                name="national"
                value={form.national}
                onChange={handleChange}
              />
            </div>
            <div>
              <label>شماره اقتصادی</label>
              <input
                name="eco"
                value={form.eco}
                onChange={handleChange}
              />
            </div>
            <div>
              <label>کد پستی</label>
              <input
                name="postal"
                value={form.postal}
                onChange={handleChange}
              />
            </div>
            <div>
              <label>استان</label>
              <input
                name="province"
                value={form.province}
                onChange={handleChange}
              />
            </div>
            <div>
              <label>شهرستان</label>
              <input
                name="city"
                value={form.city}
                onChange={handleChange}
              />
            </div>
            <div className="field-full">
              <label>نشانی کامل</label>
              <input
                name="address"
                value={form.address}
                onChange={handleChange}
              />
            </div>
            <div>
              <label>شماره تلفن/موبایل *</label>
              <input
                name="phone"
                value={form.phone}
                required
                onChange={handleChange}
              />
            </div>
          </div>
          <div className="flexend">
            <button className="btn" type="submit" disabled={saving}>
              {!saving && <i className="fa-solid fa-user-plus" />}
              <span className="btn-label">
                {saving ? 'در حال افزودن...' : 'افزودن مشتری'}
              </span>
              {saving && (
                <span className="btn-spinner">
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 50 50"
                    style={{ animation: 'spin 0.8s linear infinite' }}
                  >
                    <circle
                      cx="25"
                      cy="25"
                      r="20"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="5"
                      strokeLinecap="round"
                      strokeDasharray="90 150"
                    />
                  </svg>
                </span>
              )}
            </button>
          </div>
        </form>
      </div>

      {/* ============ لیست مشتریان ============ */}
      <div className="card">
        <h2>لیست مشتریان</h2>
        <div className="overflow">
          <table>
            <thead>
              <tr>
                <th>نام</th>
                <th>شماره ملی/ثبت</th>
                <th>شماره اقتصادی</th>
                <th>موجودی (ریال)</th>
                <th>استان</th>
                <th>شهرستان</th>
                <th>تلفن</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {customers.length === 0 ? (
                <tr>
                  <td colSpan="8" className="empty">
                    هنوز مشتری‌ای ثبت نشده است.
                  </td>
                </tr>
              ) : (
                customers.map((c) => (
                  <tr key={c.id}>
                    <td>{c.name}</td>
                    <td>{c.national || '-'}</td>
                    <td>{c.eco || '-'}</td>
                    <td
                      style={{
                        direction: 'ltr',
                        fontWeight: 700,
                        color:
                          Number(c.balance) < 0
                            ? 'var(--danger)'
                            : Number(c.balance) > 0
                            ? 'var(--success)'
                            : 'var(--muted)',
                      }}
                    >
                      {Number(c.balance || 0).toLocaleString('fa-IR')}
                    </td>
                    <td>{c.province || '-'}</td>
                    <td>{c.city || '-'}</td>
                    <td>{c.phone || '-'}</td>
                    <td>
                      <div className="row-actions">
                        <Link
                          className="btn secondary small"
                          to={`/dashboard/customers/${c.id}/edit`}
                        >
                          <i className="fa-solid fa-pen" />
                          <span className="btn-label">ویرایش</span>
                        </Link>
                        <button
                          type="button"
                          className="btn danger small"
                          onClick={() => handleDelete(c.id)}
                          disabled={deletingId === c.id}
                        >
                          {deletingId !== c.id && (
                            <i className="fa-solid fa-trash" />
                          )}
                          <span className="btn-label">
                            {deletingId === c.id
                              ? 'در حال حذف...'
                              : 'حذف'}
                          </span>
                          {deletingId === c.id && (
                            <span className="btn-spinner">
                              <svg
                                width="14"
                                height="14"
                                viewBox="0 0 50 50"
                                style={{
                                  animation: 'spin 0.8s linear infinite',
                                }}
                              >
                                <circle
                                  cx="25"
                                  cy="25"
                                  r="20"
                                  fill="none"
                                  stroke="currentColor"
                                  strokeWidth="5"
                                  strokeLinecap="round"
                                  strokeDasharray="90 150"
                                />
                              </svg>
                            </span>
                          )}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
};

export default Customers;
